import { AgGridReact } from "ag-grid-react";
import { segmentTrackToggleInQueueTableFilter } from "in_queue/analytics";
import { useAdditionalColumnsForTable } from "in_queue/contexts/AdditionalColumnConfigsContext";
import { useRegionConfig } from "in_queue/contexts/ClusterContext";
import { useCurrentProjectId } from "in_queue/contexts/ScenarioPageContext";
import { useScenarioResults } from "in_queue/contexts/ScenarioResultsContext";
import { useScenarioResultValidViewTypes } from "in_queue/hooks/useScenarioResultViewTypes";
import {
    InQueueRegionConfig,
    RelatedProjectsTypeEnum
} from "in_queue/types/configPerRegion";
import { ScenarioAllocatedUpgrade } from "in_queue/types/scenarioResultType";
import { useMemo, useRef, useState } from "react";
import { mapLoadingState } from "types/loadingType";
import { spliceAdditionalColumns } from "./agGrid/additionalColumns";
import {
    AgGridColumnDefs,
    AllocatedUpgradesTableColumnId,
    constraintTypeFormatter,
    currencyFormatter,
    dfaxFormatter,
    hideUnwantedColumns,
    roundingFormatter,
    SCENARIO_TABLE_DEFAULT_COL_DEF
} from "./agGrid/agGridHelpers";
import { doesAllocatedUpgradePassFilter } from "./agGrid/scenarioAllocatedUpgradesFilter";
import { BaseAgGrid } from "./base/BaseAgGrid";
import { ExportButton } from "./ribbon/ExportButton";
import { FilterSearchInput } from "./ribbon/FilterSearchInput";
import { ResultsCount } from "./ribbon/ResultsCount";
import { TableActionRibbon } from "./ribbon/TableActionRibbon";
import { ViewType, ViewTypesMenu } from "./ribbon/ViewTypesMenu";
import css from "./TableStyles.module.scss";

const SCENARIO_ALLOCATED_UPGRADES_ANCHOR_TO_COL_ID: Readonly<
    Record<string, AllocatedUpgradesTableColumnId>
> = {
    project_id: "projectId",
    network_upgrade_name: "networkUpgradeName",
    facility_names: "facilityNames",
    notes: "notes"
};

export const ScenarioAllocatedUpgradesTable: React.FC<{
    filterText: string;
    setFilterText: (value: string) => void;
    /** v2 sticky-header / no-inner-scroll mode — see ScenarioProjectsTable. */
    stickyHeader?: boolean;
}> = ({ filterText, setFilterText, stickyHeader }) => {
    const gridRef = useRef<AgGridReact<ScenarioAllocatedUpgrade>>(null);
    const { scenarioResults, isLoadingRemaining } = useScenarioResults();
    const componentConfig = useRegionConfig();
    const currentProjectId = useCurrentProjectId();
    const [viewType, setViewType] = useState<ViewType>("current");
    const [visibleCount, setVisibleCount] = useState<number | undefined>(
        undefined
    );
    const validViewTypes = useScenarioResultValidViewTypes();
    const additionalColumnConfigs = useAdditionalColumnsForTable(
        "scenario_allocated_upgrades"
    );
    const columnDefs = useMemo(
        () =>
            spliceAdditionalColumns(
                getColumnDefs(componentConfig),
                additionalColumnConfigs,
                SCENARIO_ALLOCATED_UPGRADES_ANCHOR_TO_COL_ID
            ),
        [componentConfig, additionalColumnConfigs]
    );

    const upgrades = mapLoadingState(
        scenarioResults,
        (d) => d.allocatedUpgrades
    );
    const totalCount =
        upgrades !== "loading" && !isLoadingRemaining
            ? upgrades.length
            : undefined;

    const leftActions = [
        <ViewTypesMenu
            currentViewType={viewType}
            setCurrentViewType={(newViewType: ViewType) => {
                segmentTrackToggleInQueueTableFilter({
                    tableName: "Allocated Upgrades",
                    filterName: newViewType
                });
                setViewType(newViewType);
            }}
            validViewTypes={validViewTypes}
        />
    ];
    const rightActions = [
        <ExportButton gridRef={gridRef} exportName="Assigned upgrades" />,
        <FilterSearchInput value={filterText} onChange={setFilterText} />
    ];

    return (
        <div className={css["table-ribbon-wrapper"]}>
            <TableActionRibbon
                leftActions={leftActions}
                rightActions={rightActions}
                sticky={stickyHeader}
            />
            <BaseAgGrid<ScenarioAllocatedUpgrade>
                ref={gridRef}
                loadingData={upgrades}
                columnDefs={columnDefs}
                analyticsTableName="Allocated Upgrades"
                stickyHeader={stickyHeader}
                defaultColDef={SCENARIO_TABLE_DEFAULT_COL_DEF}
                rowHeight={52}
                quickFilterText={filterText}
                onModelUpdated={(event) =>
                    setVisibleCount(event.api.getDisplayedRowCount())
                }
                isExternalFilterPresent={() => viewType !== "all"}
                doesExternalFilterPass={(node) => {
                    const row = node.data;
                    if (row == null) {
                        return false;
                    }
                    return doesAllocatedUpgradePassFilter(
                        row,
                        viewType,
                        currentProjectId,
                        scenarioResults === "loading"
                            ? new Set<string>()
                            : scenarioResults.relatedProjectIds,
                        componentConfig.relatedProjectsType ===
                            RelatedProjectsTypeEnum.SharedMonitoredFacilities
                    );
                }}
            />
            <div className={css["table-footer"]}>
                <ResultsCount visible={visibleCount} total={totalCount} />
            </div>
        </div>
    );
};

const getColumnDefs = (config: InQueueRegionConfig) => {
    const columnDefs: AgGridColumnDefs<
        ScenarioAllocatedUpgrade,
        AllocatedUpgradesTableColumnId
    > = [
        {
            colId: "projectId",
            field: "projectId",
            headerName: "Project",
            width: 120,
            sort: "asc",
            pinned: true,
            filter: true
        },
        {
            colId: "networkUpgradeId",
            field: "networkUpgradeId",
            headerName: "Network upgrade ID",
            width: 120,
            pinned: true,
            filter: true,
            wrapHeaderText: true
        },
        {
            colId: "networkUpgradeName",
            field: "networkUpgradeName",
            headerName: "Network upgrade",
            width: 300,
            pinned: true,
            filter: true
        },
        {
            colId: "constraintType",
            field: "constraintType",
            headerName: "Type",
            width: 80,
            filter: true,
            valueFormatter: constraintTypeFormatter
        },
        {
            headerName: "Cost calculation",
            children: [
                {
                    colId: "allocatedCost",
                    field: "allocatedCost",
                    type: "numericColumn",
                    wrapHeaderText: true,
                    autoHeaderHeight: true,
                    width: 120,
                    sort: "desc",
                    valueFormatter: currencyFormatter
                },
                {
                    colId: "totalCost",
                    field: "totalCost",
                    type: "numericColumn",
                    width: 100,
                    valueFormatter: currencyFormatter
                },
                {
                    colId: "percentAllocation",
                    field: "percentAllocation",
                    type: "numericColumn",
                    wrapHeaderText: true,
                    width: 100,
                    valueFormatter: dfaxFormatter
                },
                {
                    colId: "maxDfax",
                    field: "maxDfax",
                    type: "numericColumn",
                    headerName: config.maxDfaxLabel,
                    wrapHeaderText: true,
                    autoHeaderHeight: true,
                    width: 100,
                    valueFormatter: dfaxFormatter
                },
                {
                    colId: "sumMwImpact",
                    field: "sumMwImpact",
                    type: "numericColumn",
                    headerName: "Sum MW Impact",
                    wrapHeaderText: true,
                    autoHeaderHeight: true,
                    width: 100,
                    valueFormatter: roundingFormatter
                }
            ]
        },
        {
            colId: "facilityNames",
            field: "monitoredFacilityNames",
            headerName: "Monitored facilities",
            minWidth: 500,
            flex: 2
        },
        {
            colId: "notes",
            field: "notes",
            headerName: "Notes",
            minWidth: 300,
            flex: 1
        }
    ];

    hideUnwantedColumns(
        columnDefs,
        config.allocatedUpgradesTable.columnsToRemove
    );

    return columnDefs;
};
